import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CourseService from '../../../Api/CourseService';

const ViewCourse = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const courseId = new URLSearchParams(location.search).get('id');
    const CourseAPI = new CourseService();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [courseData, setCourseData] = useState(null);

    useEffect(() => {
        const fetchCourse = async () => {
            try {
                setLoading(true);
                const response = await CourseAPI.GetById(courseId);
                if (response.data && response.data.data) {
                    setCourseData(response.data.data);
                }
            } catch (err) {
                setError('Failed to load course data. Please try again.');
                console.error('Error loading course:', err);
            } finally {
                setLoading(false);
            }
        };

        if (courseId) {
            fetchCourse();
        } else {
            navigate('/course-list');
        }
    }, [courseId, navigate]);

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger m-4" role="alert">
                {error}
            </div>
        );
    }

    if (!courseData) {
        return (
            <div className="alert alert-warning m-4" role="alert">
                Course not found.
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-24">
                    <h4 className="text-xl text-primary-light">View Course</h4>
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => navigate('/course-list')}
                    >
                        <Icon icon="mdi:arrow-left" /> Back to List
                    </button>
                </div>

                <div className="row g-4">
                    <div className="col-md-4">
                        {courseData.thumbnail_url && (
                            <div className="mb-4">
                                <img 
                                    src={courseData.thumbnail_url} 
                                    alt="Course thumbnail" 
                                    className="img-fluid rounded"
                                    style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                />
                            </div>
                        )}

                        <div className="info-group mb-3">
                            <label className="text-muted">Price</label>
                            <p className="h5">₹{courseData.price}</p>
                        </div>

                        <div className="info-group mb-3">
                            <label className="text-muted">Maximum Students</label>
                            <p className="h5">{courseData.max_students}</p>
                        </div>

                        <div className="info-group mb-3">
                            <label className="text-muted">Current Students</label>
                            <p className="h5">{courseData.current_students || 0}</p>
                        </div>

                        <div className="info-group mb-3">
                            <label className="text-muted">Status</label>
                            <p>
                                <span className={`badge ${courseData.is_sold_out ? 'bg-danger' : 'bg-success'}`}>
                                    {courseData.is_sold_out ? 'Sold Out' : 'Available'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="col-md-8">
                        <div className="info-group mb-4">
                            <label className="text-muted">Course Title</label>
                            <h3>{courseData.title}</h3>
                        </div>

                        <div className="info-group mb-4">
                            <label className="text-muted">Description</label>
                            <p className="text-justify">{courseData.description}</p>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="info-group">
                                    <label className="text-muted">Validity (days)</label>
                                    <p className="h5">{courseData.validity_days}</p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="info-group">
                                    <label className="text-muted">Session Points</label>
                                    <p className="h5">{courseData.session_points}</p>
                                </div>
                            </div>
                        </div>

                        {courseData.caution && (
                            <div className="info-group mb-4">
                                <label className="text-muted">Caution</label>
                                <p className="text-danger">{courseData.caution}</p>
                            </div>
                        )}

                        <div className="categories-section mt-4">
                            <h5 className="mb-3">Categories</h5>
                            {courseData.categories?.map((category, index) => (
                                <div key={index} className="card mb-3">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">{category.name}</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Title</th>
                                                        <th>Video URL</th>
                                                        <th>Duration (seconds)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {category.videos?.map((video, vIndex) => (
                                                        <tr key={vIndex}>
                                                            <td>{video.title}</td>
                                                            <td>
                                                                <a href={video.vimeo_url} target="_blank" rel="noopener noreferrer">
                                                                    {video.vimeo_url}
                                                                </a>
                                                            </td>
                                                            <td>{video.video_duration}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewCourse; 